import { useFlag } from '@reflag/react-sdk';

import type * as api from '@gitbook/api-client';
import { createPalette, PaletteButton, PaletteCommandType } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { type ButtonProps, DialogRoot, useDialogTriggerState } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../../navigation';
import { SidebarActionsButton } from '../../../Sidebars/SidebarActionsButton';
import { apiClient, type SiteCapability, type FeatureUpgradeAction } from '../../../state';
import { useSiteAudienceCommand } from '../../AdaptiveContent';
import { useDuplicateSpaceCommand } from '../../Space';
import { CreateSiteSectionGroupDialog } from './CreateSiteSectionGroupDialog';
import {
    CreateSiteVariantDialog,
    type CreateSiteVariantDialogProps,
} from './CreateSiteVariantDialog';
import { getPaletteLocationOptions } from './getPaletteLocationOptions';
import { getPaletteSpaceOptions } from './getPaletteSpaceOptions';
import { getRestrictedSiteItemPaletteGroups } from './getRestrictedSiteItemPaletteGroups';
import { type UseDeleteSiteItemsProps, useDeleteSiteItems } from './useDeleteSiteItems';
import { type UseMoveSiteSectionsProps, useMoveSiteSections } from './useMoveSiteSections';
import {
    type UseSetDefaultSiteSectionProps,
    useSetDefaultSiteSection,
} from './useSetDefaultSiteSection';
import { useSetSiteSpaceVisibility } from './useSetSiteSpaceVisibility';
import { useSiteContentDraftCommand } from './useSiteContentDraft';

export interface SiteSectionActionsButtonProps
    extends
        CreateSiteVariantDialogProps,
        Pick<ButtonProps, 'size' | 'disabled'>,
        Omit<UseMoveSiteSectionsProps, 'createSiteSectionGroupDialogTrigger'>,
        UseDeleteSiteItemsProps,
        UseSetDefaultSiteSectionProps {
    /**
     * Controls whether the trigger should match sidebar item actions.
     */
    kind?: 'default' | 'sidebar';
    site: api.Site;
    organization: api.Organization;
    siteSection: api.SiteSection;
    sitePermissions: SiteCapability;
    siteStructure: api.SiteStructure;
    upgrade: FeatureUpgradeAction | undefined;
    onCreateSectionGroup?: (siteSectionGroup: api.SiteSectionGroup) => void;
}

/**
 * Action button to open options palette for a section
 */
export function SiteSectionActionsButton(props: SiteSectionActionsButtonProps) {
    const {
        site,
        organization,
        siteSection,
        sitePermissions,
        siteStructure,
        size,
        disabled,
        kind = 'default',
        onCreateVariant,
        onCreateSectionGroup,
        upgrade,
    } = props;
    const { canCreateSite, canEditSite } = sitePermissions;
    const organizationId = organization.id;
    const { navigate } = useNavigation();
    const hideSiteSectionFeature = useFlag('HIDDEN_SITES_SPACE');

    const createSiteVariantDialogTrigger = useDialogTriggerState();
    const createSiteSectionGroupDialogTrigger = useDialogTriggerState();

    const deleteSiteItems = useDeleteSiteItems(props);
    const moveSiteSections = useMoveSiteSections({ ...props, createSiteSectionGroupDialogTrigger });
    const setAsDefault = useSetDefaultSiteSection(props);
    const siteAudience = useSiteAudienceCommand({
        organizationId,
        site,
        target: 'section',
        condition: siteSection.condition ? siteSection.condition : undefined,
        onChange: async (condition) => {
            await apiClient.siteSections.updateSiteSectionById(
                organizationId,
                site.id,
                siteSection.id,
                {
                    condition: condition ?? null,
                },
                { returnPreference: 'minimal' }
            );
        },
    });
    const singleSiteSpace = siteSection.siteSpaces.length === 1 ? siteSection.siteSpaces[0] : null;
    const duplicateSpace = useDuplicateSpaceCommand(
        singleSiteSpace?.space ?? null,
        singleSiteSpace
            ? {
                  siteId: site.id,
                  sitePermissions: site.permissions,
                  siteSpaceId: singleSiteSpace.id,
                  siteItemType: 'section',
              }
            : undefined,
        upgrade
    );
    const areAllSiteSpacesHidden =
        siteSection.siteSpaces.length > 0 &&
        siteSection.siteSpaces.every((siteSpace) => siteSpace.hidden);

    const setSiteVisibility = useSetSiteSpaceVisibility({
        organization,
        site,
        type: 'section',
    });

    const draftCommand = useSiteContentDraftCommand({
        organization,
        site,
        siteStructure,
        content: siteSection,
        canCreateSite,
    });
    const draftOnlyGroup = { key: 'draft-options', commands: [draftCommand] };
    const createVariantGroup = canEditSite
        ? {
              key: 'create-variant',
              commands: [
                  {
                      key: 'siteSection.createVariant',
                      title: 'Add variant',
                      icon: Icon.Plus,
                      trigger: () => createSiteVariantDialogTrigger.open(),
                  },
              ],
          }
        : null;
    const restrictedPalette = upgrade
        ? () =>
              createPalette({
                  groups: [
                      siteSection.urls.published
                          ? getPaletteLocationOptions(siteSection.urls.published, {
                                openTitle: 'Open in site',
                            })
                          : null,
                      singleSiteSpace
                          ? getPaletteSpaceOptions({
                                space: singleSiteSpace.space,
                                siteId: site.id,
                                navigate,
                            })
                          : null,
                      createVariantGroup,
                      ...getRestrictedSiteItemPaletteGroups({
                          item: siteSection,
                          upgrade,
                          deleteSiteItems,
                          sitePermissions,
                      }),
                  ].filter(filterOutNullable),
              })
        : undefined;

    return (
        <>
            {siteAudience?.dialogs}
            {duplicateSpace.dialog}

            <DialogRoot {...createSiteVariantDialogTrigger.rootProps}>
                <CreateSiteVariantDialog {...props} onCreateVariant={onCreateVariant} />
            </DialogRoot>

            <DialogRoot {...createSiteSectionGroupDialogTrigger.rootProps}>
                <CreateSiteSectionGroupDialog
                    organization={organization}
                    site={site}
                    siteSections={[siteSection]}
                    onCreateSectionGroup={onCreateSectionGroup}
                />
            </DialogRoot>

            {kind === 'sidebar' ? (
                <SidebarActionsButton
                    palette={() => {
                        if (restrictedPalette) {
                            return restrictedPalette();
                        }
                        return createPalette({
                            groups: [
                                {
                                    key: 'edit',
                                    commands: [
                                        {
                                            key: 'siteSection.edit',
                                            title: 'Edit navigation item',
                                            icon: Icon.Edit,
                                            disabled: !canEditSite,
                                            subtitle: canEditSite
                                                ? undefined
                                                : 'You need permission to edit this site navigation.',
                                            trigger: () => {
                                                navigate({
                                                    screen: {
                                                        type: 'site',
                                                        organization: organization.id,
                                                        site: site.id,
                                                        tab: {
                                                            type: 'structure',
                                                            selected: siteSection.id,
                                                        },
                                                    },
                                                });
                                            },
                                        },
                                    ],
                                },
                                siteSection.urls.published
                                    ? getPaletteLocationOptions(siteSection.urls.published, {
                                          openTitle: 'Open in site',
                                      })
                                    : null,
                                singleSiteSpace
                                    ? {
                                          key: 'duplicate',
                                          commands: [duplicateSpace.command()].filter(
                                              (command) => command !== null
                                          ),
                                      }
                                    : null,
                                createVariantGroup,
                                canCreateSite
                                    ? {
                                          key: 'update-options',
                                          commands: [
                                              {
                                                  key: 'siteSection.setDefault',
                                                  title: 'Set as home',
                                                  icon: Icon.Home,
                                                  disabled:
                                                      Boolean(siteSection.default) ||
                                                      areAllSiteSpacesHidden,
                                                  subtitle: areAllSiteSpacesHidden
                                                      ? 'Hidden from site navigation'
                                                      : siteSection.default
                                                        ? 'Already set as home'
                                                        : undefined,
                                                  trigger: () => setAsDefault(siteSection),
                                              },
                                              draftCommand,
                                              hideSiteSectionFeature.isEnabled &&
                                              siteSection.siteSpaces.length > 0
                                                  ? {
                                                        key: 'siteSection.hide',
                                                        enabled: !siteSection.hidden,
                                                        disabled: Boolean(siteSection.default),
                                                        subtitle: siteSection.default
                                                            ? "Home section can't be hidden"
                                                            : undefined,
                                                        type: PaletteCommandType.SWITCH as const,
                                                        title: 'Show in site navigation',
                                                        icon: Icon.Eye,
                                                        trigger: () => {
                                                            setSiteVisibility(siteSection);
                                                        },
                                                    }
                                                  : null,
                                              siteAudience?.command ?? null,
                                              {
                                                  key: 'siteSection.addToGroup',
                                                  title: 'Move to…',
                                                  icon: Icon.ArrowRight,
                                                  descendant: () => moveSiteSections([siteSection]),
                                              },
                                              {
                                                  key: 'siteSection.delete',
                                                  title: 'Remove',
                                                  icon: Icon.Trashcan,
                                                  kind: 'danger' as const,
                                                  trigger: () => deleteSiteItems([siteSection]),
                                              },
                                          ].filter((x) => x !== null),
                                      }
                                    : draftOnlyGroup,
                            ].filter((x) => x !== null),
                        });
                    }}
                    disabled={disabled}
                />
            ) : (
                <PaletteButton
                    palette={() => {
                        if (restrictedPalette) {
                            return restrictedPalette();
                        }
                        return createPalette({
                            groups: [
                                siteSection.urls.published
                                    ? getPaletteLocationOptions(siteSection.urls.published, {
                                          openTitle: 'Open in site',
                                      })
                                    : null,
                                createVariantGroup,
                                canCreateSite
                                    ? {
                                          key: 'update-options',
                                          commands: [
                                              {
                                                  key: 'siteSection.setDefault',
                                                  title: 'Set as home',
                                                  icon: Icon.Home,
                                                  disabled:
                                                      Boolean(siteSection.default) ||
                                                      areAllSiteSpacesHidden,
                                                  subtitle: areAllSiteSpacesHidden
                                                      ? 'Hidden from site navigation'
                                                      : siteSection.default
                                                        ? 'Already set as home'
                                                        : undefined,
                                                  trigger: () => setAsDefault(siteSection),
                                              },
                                              draftCommand,
                                              hideSiteSectionFeature.isEnabled &&
                                              siteSection.siteSpaces.length > 0
                                                  ? {
                                                        key: 'siteSection.hide',
                                                        enabled: !siteSection.hidden,
                                                        disabled: Boolean(siteSection.default),
                                                        subtitle: siteSection.default
                                                            ? "Home section can't be hidden"
                                                            : undefined,
                                                        type: PaletteCommandType.SWITCH as const,
                                                        title: 'Show in site navigation',
                                                        icon: Icon.Eye,
                                                        trigger: () => {
                                                            setSiteVisibility(siteSection);
                                                        },
                                                    }
                                                  : null,
                                              siteAudience?.command ?? null,
                                              {
                                                  key: 'siteSection.addToGroup',
                                                  title: 'Move to…',
                                                  icon: Icon.ArrowRight,
                                                  descendant: () => moveSiteSections([siteSection]),
                                              },
                                              {
                                                  key: 'siteSection.delete',
                                                  title: 'Remove',
                                                  icon: Icon.Trashcan,
                                                  kind: 'danger' as const,
                                                  trigger: () => deleteSiteItems([siteSection]),
                                              },
                                          ].filter((x) => x !== null),
                                      }
                                    : draftOnlyGroup,
                            ].filter((x) => x !== null),
                        });
                    }}
                    kind="blank"
                    label="Actions"
                    leadingIcon={Icon.MoreVertical}
                    iconOnly
                    size={size}
                    disabled={disabled}
                />
            )}
        </>
    );
}
